//@flow

import * as React from 'react';
import * as Redux from 'redux';
import { connect } from 'react-redux';
import _ from 'lodash';
import styled from 'styled-components';
import DefaultButton from './../../../components/DefaultButton';
import GhostButton from './../../../components/GhostButton';
import Selectinput from './../../../components/SelectInput';
import type { Flight } from './../../../hocs/flightManager';
import { addFlight, editFlight, clearMessage, setFlightGeometryType } from './../../../actions/flightManager';
import { fieldsDictionary } from './../../../utilities/flightDictionaries';
import { renderAttributeEdit } from './../../../utilities/renderAttributes.jsx'
import { startDrawingFlightGeometry, finishDrawingFlightGeometry, cancelDrawingFlightGeometry, setEditMode } from './../../../actions/map';
import { translate } from "react-i18next";
import messageManager from './../../../utilities/messageManager';

type Message = {
    status: string,
    text: string,
}

type Props = {
    visible: boolean,
    children: React.Node,
    axiosInstance: Object,
    chosenFlight: Flight,
    rawData: Array<Flight>,
    addFlight: (axiosInstance: Object, newFlight: Flight) => void,
    message: Message,
    clearMessage: () => void,
    startDrawingFlightGeometry: (flight: Flight) => void,
    t: (s: string) => string,
    closeSelf: () => void,
    openSelf: () => void,
    setFlightGeometryType: (geometryType: string) => void,
    flightGeometryType: string,
}

type State = {
    editedFlight: Flight,
    flightsNumber: number,
    activePage: number,
    isDrawing: boolean,
};

const SidebarCover = styled.div`
    height: calc(100% - 32px);
    width: 100%;
    position: absolute;
    z-index: 1;
    background-color: #fcfcfc;
    color: #1f1f1f;
    top: 32px;
    display:flex;
    flex-direction: column;
    align-items: center;
`;

const SidebarContent = styled.div`
    display: flex;
    flex-direction: column;
    width: calc(100% - 20px);
    max-height: calc(100% - 60px);
    overflow: overlay;
`;
const CreatorTitle = styled.h3`
    margin: 10px 0 5px;
    color: #265a9c;
    font-size: 16px;
    font-weight: normal;
    text-align: center;
`;

const CreatorRow = styled.div`
    display: flex;
    flex-direction: column;
    margin: 0 0 5px 5px;
    span {
        margin: 0 0 2px 0;
        font-size: 13px;
        color: #4b4b4b;
    }
    input, select{
        border: 1px solid #bfbfbf;
        color: #265a9c;
        font-weight: 600;
        font-family: "Open Sans", sans-serif;
        line-height: 16px;
    }
    input {
        padding: 2px 4px;
        width: calc(100% - 20px);
        ::placeholder{
            font-weight: 500;
        }
    }
    select {
        width: calc(100% - 10px);
        padding: 2px 4px;
    }
`;

const InputContainer = styled.div`
    padding: 5px;
    height: calc(100% - 46px);
    width: calc(100% - 10px);
    overflow: auto;
`;

const Description = styled.p`
    white-space: pre-wrap;
    font-size: 14px;
    color: #265a9c;
`;

const Tip = styled.p`
    white-space: pre-wrap;
`;

const ButtonWrapper = styled.div`
    display: flex;    
`;

class FlightCreator extends React.Component<Props, State> {
    constructor() {
        super();
        const editedFlight = this.getClearFeature();
        this.state = {
            editedFlight: editedFlight,
            activePage: 1,
            isDrawing: false,
        }
    }

    getClearFeature() {
        const editedFlight = {};
        for (const attribute in fieldsDictionary) {
            switch (fieldsDictionary[attribute].renderType) {
                case 'select':
                    editedFlight[attribute] = 0;
                    break;
                default:
                    editedFlight[attribute] = '';
                    break;
            }
        }
        editedFlight.id = 0;
        editedFlight.flightStatus = 1;
        return editedFlight;
    }

    componentDidUpdate(prevProps) {
        const { message, t, clearMessage, chosenFlight, visible } = this.props;
        
        if (message.text !== "") {
            clearMessage();
            const translatedMessage = {
                text: t(message.text),
                type: message.type
            };
            messageManager.show(translatedMessage);
        }

        if (visible && !prevProps.visible) {
            this.setState({
                editedFlight: chosenFlight ? _.cloneDeep(chosenFlight) : this.getClearFeature()
            });
        }

        if (prevProps.drawingFlight !== this.props.drawingFlight
            && (this.props.drawingFlight === 'finished' || this.props.drawingFlight === 'canceled')) {
            this.setState({
                isDrawing: false
            })
        }
    }

    renderFlightForm() {
        const { products, t } = this.props;
        const { activePage, editedFlight } = this.state;
        return Object.keys(fieldsDictionary).filter(attribute => {
                return fieldsDictionary[attribute].visible && fieldsDictionary[attribute].isEditable;
            }).map(attribute => {
                const { header, unit } = fieldsDictionary[attribute]
                if (activePage > 1)
                    return (
                        <CreatorRow
                            key={`${header}_creator_input`}>
                            <span>
                                {`${t(header)} ${unit ? '[' + unit + ']' : ''}`}
                            </span>
                            <span>
                                {
                                    renderAttributeEdit(editedFlight.id, fieldsDictionary[attribute], editedFlight[attribute], (newValue) => {
                                    editedFlight[attribute] = newValue;
                                    this.setState({
                                        editedFlight
                                    });
                                }, t, products)}
                            </span>
                        </CreatorRow>
                    )
                else return '';
            })
    }

    render() {
        const { axiosInstance, t } = this.props;
        if (this.props.visible) {
            const { activePage } = this.state;
            if (activePage === 1) {
                return (
                    <SidebarCover>
                        <SidebarContent>
                            <CreatorTitle>
                                {(this.state.editedFlight.id === 0)
                                    ? <b>{`${t('New mission')}`}</b>
                                    : <b>{`${t('Edit mission')}`}</b>
                                }
                                {` - ${t('step')} ${activePage}/2`}
                            </CreatorTitle>

                            <Description>
                                {t('You can draw mission geometry')}
                            </Description>

                            <Tip>
                                {t('Select project geometry type')}:
                            </Tip>
                            <Selectinput
                                style={{
                                    width: '162px',
                                }}
                                onChange={(e) => {
                                    this.props.setFlightGeometryType(e.target.value);
                                }}
                                value={this.props.flightGeometryType}
                                disabled={this.state.isDrawing}
                            >
                                <option value="Point">{t('Point')}</option>
                                <option value="LineString">{t('Line')}</option>
                                <option value="Polygon">{t('Polygon')}</option>
                            </Selectinput>

                            <Tip>
                                {t('Click to start drawing geometry on the map')}
                                {this.props.flightGeometryType === 'Point'
                                    ? ''
                                    : ` ${t('Double click on the map to finish')}`
                                }
                            </Tip>
                            <ButtonWrapper>
                                <GhostButton
                                    style={{
                                        height: '25px',
                                        width: '130px',
                                        marginRight: '5px',
                                    }}
                                    visible={this.state.isDrawing}
                                    title={t('Finish drawing')}
                                    onClick={() => {
                                        this.props.finishDrawingFlightGeometry();
                                        this.setState({
                                            isDrawing: false
                                        })
                                    }}>
                                    {t('Finish drawing')}
                                </GhostButton>
                                <DefaultButton
                                    style={{
                                        height: '25px',
                                        width: '130px',
                                    }}
                                    visible={!this.state.isDrawing}
                                    title={t('Draw geometry')}
                                    onClick={() => {
                                        const { editedFlight } = this.state;
                                        editedFlight.geometryType = this.props.flightGeometryType;
                                        this.props.startDrawingFlightGeometry(editedFlight);
                                        this.setState({
                                            isDrawing: true
                                        });
                                    }}>
                                    {t('Draw geometry')}
                                </DefaultButton>
                            </ButtonWrapper>
                            <GhostButton
                                style={{
                                    position: 'absolute',
                                    height: '25px',
                                    right: '85px',
                                    bottom: '10px',
                                }}
                                title={t("Cancel")}
                                onClick={() => {
                                    this.props.closeSelf();
                                    this.props.cancelDrawingFlightGeometry();
                                    this.setState({
                                        isDrawing: false
                                    });
                                    this.props.setEditMode(false);
                                }}>
                                {t("Cancel")}
                            </GhostButton>

                            <DefaultButton
                                style={{
                                    position: 'absolute',
                                    height: '25px',
                                    width: '55px',
                                    right: '20px',
                                    bottom: '10px',
                                }}
                                title={t('Next')}
                                onClick={() => {
                                    this.props.finishDrawingFlightGeometry();
                                    this.setState({ activePage: 2 })
                                }}>
                                {t('Next')}
                            </DefaultButton>
                        </SidebarContent>
                    </SidebarCover>
                );
            }
            if (activePage === 2) {
                return (
                    <SidebarCover>
                        <SidebarContent>
                            <CreatorTitle>
                                {(this.state.editedFlight.id === 0)
                                    ? <b>{`${t('New mission')}`}</b>
                                    : <b>{`${t('Edit mission')}`}</b>
                                }
                                {` - ${t('step')} ${activePage}/2`}
                            </CreatorTitle>
                            <InputContainer>
                                {this.renderFlightForm()}
                            </InputContainer>
                            <GhostButton
                                style={{
                                    position: 'absolute',
                                    right: '85px',
                                    bottom: '10px',
                                    height: '25px',
                                }}
                                title={t('Back')}
                                onClick={() => {
                                    this.setState({ activePage: activePage - 1 })
                                }}>
                                {t('Back')}
                            </GhostButton>
                            <DefaultButton
                                style={{
                                    position: 'absolute',
                                    height: '25px',
                                    width: '55px',
                                    right: '20px',
                                    bottom: '10px',
                                }}
                                title={t('Next')}
                                onClick={() => {
                                    if (activePage === 2) {
                                        const { editedFlight } = this.state;
                                        if (editedFlight.projectName) {
                                            if (editedFlight.id === 0){
                                                this.props.addFlight(axiosInstance, editedFlight);
                                            }
                                            else{
                                                this.props.editFlight(axiosInstance, editedFlight);
                                            }
                                            this.setState({
                                                activePage: 1,
                                                isDrawing: false
                                            });
                                            this.props.closeSelf();
                                            this.props.setEditMode(false);
                                        }
                                        else {
                                            messageManager.show({
                                                type: 'error',
                                                text: t('Project name could not be empty'),
                                            })
                                        }
                                    }
                                }}>
                                {activePage === 2 ? t('Save') : t('Next')}
                            </DefaultButton>
                        </SidebarContent>
                    </SidebarCover>
                );
            }
        }
        else return null;
    }
};

const mapStateToProps = state => ({
    drawingFlight: state.mapData.get('drawingFlight'),
    axiosInstance: state.auth.get('axiosInstance'),
    chosenFlight: state.flightManager.get('chosenFlight'),
    rawData: state.flightManager.get('rawData'),
    message: state.flightManager.get('message'),
    olMap: state.mapData.get('olMap'),
    products: state.flightManager.get('products'),
    flightGeometryType: state.flightManager.get('flightGeometryType'),
});

const mapDispatchToProps = dispatch => Redux.bindActionCreators({
    addFlight,
    editFlight,
    clearMessage,
    startDrawingFlightGeometry,
    setFlightGeometryType,
    finishDrawingFlightGeometry,
    cancelDrawingFlightGeometry,
    setEditMode,
}, dispatch);

export default translate(
    "translations",
    { withRef: true }
)(connect(
    mapStateToProps,
    mapDispatchToProps,
    null,
    { withRef: true }
)(FlightCreator));


// WEBPACK FOOTER //
// ./src/containers/content/flightManager/FlightCreator.jsx